import { Link } from "react-router";
import type { UserStatsRow } from "../../lib/admin/types";
import { toDate } from "../../lib/admin/types";
import { formatPercentage } from "../../lib/admin/formatters";
import { useT } from "../../lib/use-t";
import { AdminAvatar } from "./AdminAvatar";
import { RelativeTime } from "./RelativeTime";
import { RolePill } from "./RolePill";

export function UserListTable({ users }: { users: UserStatsRow[] }) {
  const t = useT();
  if (users.length === 0) {
    return (
      <p className="rounded-xl border border-[var(--border)] bg-[var(--panel)] p-6 text-center text-[var(--ink-muted)]">
        {t("admin.users.empty")}
      </p>
    );
  }
  return (
    <>
      <ul className="md:hidden space-y-3">
        {users.map((u) => (
          <li key={u.id}>
            <Link
              to={`/admin/users/${u.id}`}
              className="block rounded-xl border border-[var(--border)] bg-[var(--panel)] p-4 space-y-3 transition-colors hover:bg-[var(--panel-soft)] active:bg-[var(--panel-soft)]"
              aria-label={t("admin.users.viewAriaFmt", { name: u.displayName })}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <AdminAvatar name={u.displayName} id={u.id} size="md" />
                  <div className="min-w-0">
                    <p className="font-semibold tracking-tight truncate">{u.displayName}</p>
                    <p className="font-mono-badge text-xs text-[var(--ink-faint)] truncate">
                      {u.identifier}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-2 justify-end shrink-0">
                  {u.roles.map((r) => (
                    <RolePill key={r} role={r} />
                  ))}
                </div>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className="text-[var(--ink-muted)]">{t("admin.users.cardSessions")}</dt>
                <dd className="text-right font-mono-badge">{u.sessionCount}</dd>
                <dt className="text-[var(--ink-muted)]">{t("admin.users.cardLast")}</dt>
                <dd className="text-right">
                  <RelativeTime when={toDate(u.lastSessionAt)} />
                </dd>
                <dt className="text-[var(--ink-muted)]">{t("admin.users.cardAverage")}</dt>
                <dd className="text-right font-mono-badge">
                  {formatPercentage(u.averagePercentage)}
                </dd>
                <dt className="text-[var(--ink-muted)]">{t("admin.users.cardPassRate")}</dt>
                <dd className="text-right font-mono-badge">
                  {formatPercentage(u.passRate)}
                </dd>
              </dl>
            </Link>
          </li>
        ))}
      </ul>
      <div className="hidden md:block rounded-xl border border-[var(--border)] bg-[var(--panel)] overflow-hidden">
        <table className="w-full text-sm">
          <caption className="sr-only">{t("admin.users.tableCaption")}</caption>
          <thead className="bg-[var(--panel-soft)] text-[var(--ink-muted)]">
            <tr>
              <th className="text-left font-medium px-5 py-3">{t("admin.users.colUser")}</th>
              <th className="text-left font-medium px-5 py-3">{t("admin.users.colRole")}</th>
              <th className="text-left font-medium px-5 py-3">{t("admin.users.colDefaultModule")}</th>
              <th className="text-right font-medium px-5 py-3">{t("admin.users.colSessions")}</th>
              <th className="text-left font-medium px-5 py-3">{t("admin.users.colLastSession")}</th>
              <th className="text-right font-medium px-5 py-3">{t("admin.users.colAverage")}</th>
              <th className="text-right font-medium px-5 py-3">{t("admin.users.colPassRate")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr
                key={u.id}
                className={
                  "group transition-colors hover:bg-[var(--panel-soft)] " +
                  (i > 0 ? "border-t border-[var(--border)]" : "")
                }
              >
                <td className="px-5 py-3">
                  <Link
                    to={`/admin/users/${u.id}`}
                    className="flex items-center gap-3 transition-colors group-hover:text-[var(--accent)]"
                    aria-label={t("admin.users.viewAriaFmt", { name: u.displayName })}
                  >
                    <AdminAvatar name={u.displayName} id={u.id} size="md" />
                    <span className="min-w-0">
                      <span className="block font-semibold tracking-tight truncate">
                        {u.displayName}
                      </span>
                      <span className="block font-mono-badge text-xs text-[var(--ink-faint)] truncate">
                        {u.identifier}
                      </span>
                    </span>
                  </Link>
                </td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-x-2">
                    {u.roles.map((r) => (
                      <RolePill key={r} role={r} />
                    ))}
                  </div>
                </td>
                <td className="px-5 py-3 font-mono-badge text-xs text-[var(--ink-muted)]">
                  {u.defaultModule ?? "—"}
                </td>
                <td className="px-5 py-3 text-right font-mono-badge">{u.sessionCount}</td>
                <td className="px-5 py-3 text-[var(--ink-muted)]">
                  <RelativeTime when={toDate(u.lastSessionAt)} />
                </td>
                <td className="px-5 py-3 text-right font-mono-badge">
                  {formatPercentage(u.averagePercentage)}
                </td>
                <td className="px-5 py-3 text-right font-mono-badge">
                  {formatPercentage(u.passRate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
